// AI-generated: please review with /code-review before committing.
import * as React from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { useAtomValue } from 'jotai';

import { translate } from '~l10n/client';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { calculateSubscriptionState } from '~teacher/components/Account/helpers';
import { useCalculateSeatsAvailable } from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog';
import { TeamSubscriptionControls } from '~teacher/components/Account/TeamSubcription/TeamSubscriptionControls';
import {
  TeamSubscriptionPlanData,
  TeamSubscriptionPlanFree,
  TeamSubscriptionPlanLoading,
} from '~teacher/components/Account/TeamSubcription/TeamSubscriptionPlan';
import { useSelectedTeamPermissionQuery } from '~teacher/components/PricingTable/hooks';
import { useTeams } from '~teacher/hook/team';

export function useSelectedSubscriptionQuery() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  return useTeams({
    select: (data) => {
      const team = data.find(({ id }) => `${id}` === `${selectedTeamId}`);
      return {
        subscriptionId: team?.subscriptionId,
        subscription: team?.subscription,
      };
    },
  });
}

function TeamSubscriptionLoading() {
  return (
    <TeamSubscriptionContainer>
      <TeamSubscriptionPlanLoading />
    </TeamSubscriptionContainer>
  );
}

export function TeamSubscription() {
  const selectedSubscription = useSelectedSubscriptionQuery();
  if (selectedSubscription.data === undefined) {
    return <TeamSubscriptionLoading />;
  }

  const { subscriptionId, subscription } = selectedSubscription.data;

  if (!subscriptionId || !subscription) {
    return <TeamSubscriptionFree />;
  }

  return <TeamSubscriptionData />;
}

function useSelectedTeamRoleQuery() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);

  return useTeams({
    select: (data) =>
      data.find(({ id }) => `${id}` === `${selectedTeamId}`)?.role,
  });
}

function TeamSubscriptionData() {
  const selectedSubscriptionQuery = useSelectedSubscriptionQuery();
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const selectedTeamRoleQuery = useSelectedTeamRoleQuery();

  const { seatsAvailable } = useCalculateSeatsAvailable(selectedTeamId);

  const teamPermissions = useSelectedTeamPermissionQuery();

  if (
    !selectedSubscriptionQuery?.data?.subscriptionId ||
    !selectedSubscriptionQuery?.data?.subscription ||
    !teamPermissions.data ||
    !selectedTeamRoleQuery.data
  ) {
    return <TeamSubscriptionLoading />;
  }

  const { plan, lookupKey, totalSeats, endDate, cancelAtEndDate, status } =
    selectedSubscriptionQuery.data.subscription;

  const planDuration = lookupKey?.includes('monthly')
    ? translate('Monthly')
    : translate('Annually');

  const subscriptionState = calculateSubscriptionState({
    status,
    endDate,
    cancelAtEndDate,
    plan,
  });

  const { canOpenStripeCheckout, canOpenStripePortal, canViewPlans } =
    teamPermissions.data;

  return (
    <TeamSubscriptionContainer>
      <TeamSubscriptionPlanData
        plan={plan}
        totalSeats={totalSeats}
        seatsAvailable={seatsAvailable}
        planDuration={planDuration}
        teamId={selectedTeamId}
        status={status}
        subscriptionState={subscriptionState}
        endDate={endDate}
        cancelAtEndDate={cancelAtEndDate}
      />
      <TeamSubscriptionControls
        planType={plan}
        subscriptionState={subscriptionState}
        canOpenStripeCheckout={canOpenStripeCheckout}
        canOpenStripePortal={canOpenStripePortal}
        canViewPlans={canViewPlans}
        teamId={selectedTeamId}
        teamRole={selectedTeamRoleQuery.data}
      />
    </TeamSubscriptionContainer>
  );
}

export function TeamSubscriptionFree() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const selectedTeamPermissionQuery = useSelectedTeamPermissionQuery();

  if (!selectedTeamPermissionQuery.data) {
    return <TeamSubscriptionLoading />;
  }

  const { canOpenStripeCheckout, canOpenStripePortal, canViewPlans } =
    selectedTeamPermissionQuery.data;

  return (
    <TeamSubscriptionContainer>
      <TeamSubscriptionPlanFree teamId={selectedTeamId} />
      <TeamSubscriptionControls
        subscriptionState="None"
        planType={PlanType.Free}
        canOpenStripeCheckout={canOpenStripeCheckout}
        canOpenStripePortal={canOpenStripePortal}
        canViewPlans={canViewPlans}
        teamId={selectedTeamId}
      />
    </TeamSubscriptionContainer>
  );
}

function TeamSubscriptionContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 col-span-2 grid-cols-subgrid">{children}</div>
  );
}
